import type { DirectHireInput } from '../features/edo/hire-types'
import type { Employment, EmploymentSummary } from "../lib/types"
import Constants from 'expo-constants'
import type {
  Post, PostDetail, PostPage, PostInput, Profile, ProfilePatch, OrgNode, PositionNode, Comment,
  Department, DepartmentTreeCompany, AbsencesResponse, AssistantAccess, AssistantConversation, AssistantConversationSummary, AssistantStepBody, AssistantStepResponse, BalancesResponse, UserPreferences, UserPreferencesPatch,
  EdoRequest, EdoTask, EdoType, EdoRouteStep, EdoDecision, ParticipatingRequest, Notification, ReactionCounts, ReactionType, Attachment,
  VacationPayload, TimeOffInput, TimeOffBalance, RequestCard, TaskCandidates,
  SickLeaveInput, AbsenceOverlap, FreeformInput, ContractInput, ContractCompany, ContractCountry, CounterpartyCard,
  PaternityLeaveInput, MaternityLeaveInput, ChildCareLeaveInput,
  BusinessTripInput, TripDestination, VacationBalance,
  RecruitmentInput, RecruitmentGrade, CloseRecruitmentInput, RecruitmentDashboard,
  DeviceRegistration, Course, CourseDetail, LearningSummary,
  CalEvent, ProductionDay, Community, CommunityDetail, Faq, MyTask, MyTaskInput, SearchResults,
  NewsItem, NewsPage, NewsInput, GraphStatus, GraphConnect, GraphSyncResult,
  KbBook, KbBookDetail, KbPage, KbSearchHit, KbConfig,
  DirectoryUserFull, UserAdminPatch, LocationItem,
  SigningOperation,
  PublicSigningCard,
  ItWorksDirection, ItWorksInput, ItWorksOptions, ItWorksLineDecisionInput,
  TripVariantInput,
} from '../lib/types'
import { getAccessToken, getDevUserEmail, notifyUnauthorized } from '../auth/tokenBridge'
import { azdb } from '../i18n/runtime-lang'
import { reportApiCall } from '../lib/monitoring'
import type {
  EmployerArticles, EmployerTerminationDraftInput, EmployerTerminationInput, EmployerTerminationReferences,
  ExitInterviewInput, TerminationDraftInput, TerminationHrInput, TerminationInput, TerminationReferences,
} from '../features/edo/termination-types'
import type { HireInput, HireReferences, HireFinResult } from '../features/edo/hire-types'

const ORIGIN = Constants.expoConfig?.extra?.apiUrl ?? 'http://localhost:3000'
const BASE = ORIGIN + '/api'

/** Auth headers shared by JSON + multipart requests (Bearer, or dev-fallback email). */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  const token = await getAccessToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  } else {
    const devEmail = getDevUserEmail()
    if (devEmail) headers['x-debug-user-email'] = devEmail
  }
  return headers
}

/**
 * Pull the human-readable NestJS `message` out of an error body so the UI can
 * show it. Backend messages are RU; `azdb` swaps the static ones to AZ when the
 * user picked `az` (messages with ${…} placeholders aren't in the dictionary and
 * stay RU). `message` may be a string or a string[] (class-validator).
 */
function errorMessage(res: Response, body: string): string {
  try {
    const j = JSON.parse(body) as { message?: string | string[] }
    const m = Array.isArray(j.message)
      ? j.message.map((x) => azdb(x)).join('; ')
      : j.message
    if (m) return azdb(m)
  } catch {
    // body wasn't JSON — fall through to the status line
  }
  return `${res.status} ${res.statusText}`.trim()
}

/** Ошибка поля из 400 (`errors: [{ field, message }]`, напр. правила подачи IT-работ). */
export interface ApiFieldError { field: string; message: string }

/** `errors[]` тела ошибки — только если это массив `{ field, message }`; иначе undefined. */
function fieldErrors(body: string): ApiFieldError[] | undefined {
  try {
    const e = (JSON.parse(body) as { errors?: unknown }).errors
    return Array.isArray(e) && e.every((x) => typeof x?.field === 'string' && typeof x?.message === 'string')
      ? (e as ApiFieldError[])
      : undefined
  } catch {
    return undefined
  }
}

/**
 * Ошибка API с HTTP-статусом: UI может отличить 403 «нет прав» от прочих сбоев (5xx/сеть).
 * `errors` — ошибки по полям, если сервер их прислал: форма подсвечивает каждое поле.
 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly errors?: ApiFieldError[]) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handle<T>(res: Response, opts?: { notifyOn401?: boolean }): Promise<T> {
  if (!res.ok) {
    // Token rejected/expired → ask auth to end the session (no-op in dev-fallback).
    // Для публичных ручек это выключено: 401 оттуда — ответ про запрос, а не про сессию,
    // и гасить чужую живую сессию в соседней вкладке он не должен.
    if (res.status === 401 && opts?.notifyOn401 !== false) notifyUnauthorized()
    const text = await res.text().catch(() => '')
    throw new ApiError(errorMessage(res, text), res.status, fieldErrors(text))
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** Shared fetch step for http/upload: times the request and reports every completed request to monitoring. */
async function fetchTracked(path: string, init: RequestInit): Promise<Response> {
  const method = init.method ?? 'GET'
  const started = Date.now()
  let res: Response
  try {
    res = await fetch(BASE + path, init)
  } catch (e) {
    reportApiCall({ method, url: '/api' + path, status: 0, durationMs: Date.now() - started })
    throw e
  }
  // Шлём каждый завершившийся запрос (латентность P75/P95 + error rate); 401/403 отфильтрует обёртка.
  reportApiCall({ method, url: '/api' + path, status: res.status, durationMs: Date.now() - started })
  return res
}

async function http<T>(path: string, opts?: RequestInit): Promise<T> {
  // `http()` всегда ставит JSON-заголовок, а для multipart это тихо ломает запрос: fetch не
  // добавит boundary, и сервер не разберёт тело. Падаем сразу и явно — это ошибка в коде,
  // а не ситуация времени выполнения. Файлы шлются через `upload()`.
  if (typeof FormData !== 'undefined' && opts?.body instanceof FormData) {
    throw new Error('FormData нельзя слать через http() — используйте upload()')
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
    ...((opts?.headers as Record<string, string>) ?? {}),
  }
  const res = await fetchTracked(path, { ...opts, headers })
  return handle<T>(res)
}

/**
 * Запрос без токена — для ручек, доступных не вошедшему человеку (восстановление пароля).
 * Токена у него нет по условию задачи, а `authHeaders()` в dev-режиме подставил бы
 * `x-debug-user-email` и прогнал бы запрос «от имени» случайного пользователя.
 */
async function publicHttp<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchTracked(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handle<T>(res)
}

/** Multipart upload — let fetch set the multipart boundary (do NOT override Content-Type). */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetchTracked(path, { method: 'POST', body: form, headers: await authHeaders() })
  return handle<T>(res)
}

/** Build a `?a=1&b=2` query string, skipping undefined/empty values (URL-encoded). */
function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, String(v))
  }
  // URLSearchParams encodes spaces as '+' (form-urlencoded); match the prior encodeURIComponent output (%20).
  const s = usp.toString().replace(/\+/g, '%20')
  return s ? `?${s}` : ''
}


// ── кабинет внешнего пользователя (поставщики, турагенты) ──
/** Укрупнённое состояние договора для контрагента: внутренние шаги согласования ему не адресованы. */
export type CabinetState =
  | 'in_review'
  | 'awaiting_signed_copy'
  | 'signed'
  | 'rejected'
  | 'cancelled'

export interface CabinetProfile {
  fullName: string
  email: string
  phone: string | null
  role: 'SIGNER' | 'SPECIALIST' | 'RESPONSIBLE'
  organization: { voen: string; name: string }
}

/** Поля приходят белым списком с бэка — внутренних (согласующие, комментарии) здесь нет. */
export interface CabinetContract {
  id: string
  number: number
  state: CabinetState
  createdAt: string
  signedAt: string | null
  title?: string
  company?: string
  docType?: string
  contractNo?: string | null
  docNo?: string | null
  subject?: string
  amount?: number
  currency?: string
  paymentType?: string
  duration?: string
  counterpartyName?: string
}

/**
 * Поездка в кабинете турагентства. Состав полей — белый список бэка: ни цели поездки, ни
 * суточных, ни комментариев согласующих агентству не отдаётся (решение 28.10.2026).
 * ФИО участника приходит ЛАТИНИЦЕЙ: именно оно идёт в билет.
 */
export interface CabinetTripVariant {
  flight: string
  cabin?: string
  hotel: string
  price: number
  currency: string
}

export interface CabinetTrip {
  id: string
  number: number
  createdAt: string
  state: 'in_progress' | 'closed'
  originCityName: string | null
  destinationCityName: string | null
  destinationAddress: string | null
  dateFrom: string | null
  dateTo: string | null
  travellers: { name: string; phone: string | null; passportValidUntil: string | null }[]
  variants: CabinetTripVariant[]
  choice: { index: number; by: 'employee' | 'auto'; at: string } | null
  /** Что ждут ОТ АГЕНТСТВА прямо сейчас. null — шаг не за ним, действий не рисуем. */
  agentStep: 'options' | 'booking' | null
  /**
   * Тред с куратором HR — единственный выход агента: вернуть заявку или отказаться он не может.
   * `awaitingAnswer` — последняя реплика его, ответа ещё нет (статус «Уточнение» в макете).
   */
  thread: { at: string; text: string; fromAgency: boolean; author: string | null }[]
  awaitingAnswer: boolean
  /**
   * Почему кадровик вернул бронь. Приходит только на ПОВТОРНОМ шаге брони: шаг открывается
   * заново и выглядит как первый, а комментариев согласующих в выдаче кабинета нет.
   */
  returnedReason: string | null
  /** Документы брони, уже приложенные агентством. Только в карточке, в списке их нет. */
  attachments?: CabinetAttachment[]
}

export interface CabinetAttachment {
  uploadId: string
  name: string
  size: number
  mime: string
  kind: string
  createdAt: string
}

export type CabinetContractDetail = CabinetContract & {
  attachments: CabinetAttachment[]
}

export const api = {
  // ── users / directory ──
  me: () => http<Profile>('/users/me'),
  /** `scope: 'all'` — поиск без скоупа филиала; сервер уважает его только для ЭДО-роли legal. */
  users: (search?: string, init?: { signal?: AbortSignal; scope?: 'all' }) =>
    http<Profile[]>(`/users${qs({ search, scope: init?.scope })}`, { signal: init?.signal }),
  userEmployments: (id: string) => http<EmploymentSummary>(`/users/${id}/employments`),
  setPrimaryEmployment: (id: string, employmentId: string) => http<EmploymentSummary>(`/users/${id}/employments/${employmentId}/primary`, { method: 'PATCH' }),
  edoEmployments: (onBehalfOfUserId?: string) => http<Employment[]>(`/edo/employments${qs({ onBehalfOfUserId })}`),
  user: (id: string) => http<Profile>(`/users/${id}`),
  orgChart: () => http<OrgNode[]>('/users/org-chart'),
  /** Оргчарт холдинга: видимые юрлица (`kind: 'company'`), под каждым — ростер людей компании. */
  orgChartHolding: () => http<PositionNode[]>('/users/org-chart/holding'),
  departments: () => http<Department[]>('/users/departments'),
  /** Юрлица → подразделения 1С → руководитель и люди (вкладка «Подразделения» оргструктуры). */
  departmentTree: () => http<DepartmentTreeCompany[]>('/users/departments/tree'),
  /** «Состояние»: кто отсутствует на дату (YYYY-MM-DD, по умолчанию — сегодня по Баку). */
  absences: (date?: string) => http<AbsencesResponse>(`/users/absences${qs({ date })}`),

  // ── ИИ-ассистент (as-life-api/docs/ai-assistant.md) ──
  /** Включён ли ассистент и доступен ли текущему сотруднику (кнопка в шапке — только при enabled). */
  assistantAccess: () => http<AssistantAccess>('/assistant/access'),
  assistantConversations: () => http<AssistantConversationSummary[]>('/assistant/conversations'),
  assistantConversation: (id: string) => http<AssistantConversation>(`/assistant/conversations/${id}`),
  createAssistantConversation: () =>
    http<AssistantConversation>('/assistant/conversations', { method: 'POST', body: JSON.stringify({}) }),
  /** Один шаг беседы: сообщение / выполнить инструмент / подтвердить / продолжить. */
  assistantStep: (id: string, body: AssistantStepBody) =>
    http<AssistantStepResponse>(`/assistant/conversations/${id}/step`, { method: 'POST', body: JSON.stringify(body) }),
  /** PATCH /users/me — только phone/skills (должность/отдел — из 1С). */
  updateMe: (data: ProfilePatch) =>
    http<Profile>('/users/me', { method: 'PATCH', body: JSON.stringify(data) }),
  /** multipart, поле "file" → { avatarUrl }. */
  uploadAvatar: (form: FormData) => upload<{ avatarUrl: string }>('/users/me/avatar', form),
  /** Остатки 1С; availableDays пока null — рисовать с фолбэком. */
  balances: () => http<BalancesResponse>('/users/me/balances'),

  // ── HR-редактирование карточек (роль hr/admin) ──
  /** Полная карточка для префилла формы правки — GET /users/:id/full. 403 без роли. */
  userFull: (id: string) => http<DirectoryUserFull>(`/users/${id}/full`),
  /** PATCH /users/:id — слать только изменённые поля; `null` = очистить. Ответ — публичный Profile. */
  updateUser: (id: string, data: UserAdminPatch) =>
    http<Profile>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** Справочник офисов для пикера локации — GET /locations (любой аутентифицированный). */
  locations: () => http<LocationItem[]>('/locations'),

  // ── восстановление пароля по почте (филиал Грузии и внешние, без AD) ──
  /** Запросить ссылку. Ответ нейтральный всегда — существование учётки он не выдаёт. */
  passwordResetRequest: (login: string) =>
    publicHttp<{ message: string }>('/auth/password-reset/request', { login }),
  /** Установить пароль по токену из письма. */
  passwordResetConfirm: (token: string, password: string) =>
    publicHttp<{ ok: true }>('/auth/password-reset/confirm', { token, password }),


  // ── кабинет внешнего пользователя ──
  /** Профиль: организация по ВÖİN и роль в ней. 403 — доступ не выдан или заблокирован. */
  cabinetMe: () => http<CabinetProfile>('/cabinet/me'),
  /** Договоры своей организации, свежие сверху. */
  cabinetContracts: () => http<CabinetContract[]>('/cabinet/contracts'),
  /** Поездки, которые обслуживает агентство этого контрагента. */
  cabinetTrips: () => http<CabinetTrip[]>('/cabinet/trips'),
  cabinetTrip: (id: string) =>
    http<CabinetTrip>(`/cabinet/trips/${encodeURIComponent(id)}`),
  /** Внести варианты и закрыть шаг — бэк проводит решение тем же путём, что у сотрудников. */
  cabinetTripOptions: (id: string, variants: CabinetTripVariant[]) =>
    http<{ ok: true }>(`/cabinet/trips/${encodeURIComponent(id)}/options`, {
      method: 'POST',
      body: JSON.stringify({ variants }),
    }),
  cabinetTripAttach: (id: string, form: FormData) =>
    upload<{ uploadId: string; name: string }>(
      `/cabinet/trips/${encodeURIComponent(id)}/attachments`,
      form,
    ),
  cabinetTripBooking: (id: string, comment?: string) =>
    http<{ ok: true }>(`/cabinet/trips/${encodeURIComponent(id)}/booking`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),
  /** Уточнение куратору HR: единственный выход агента — вернуть заявку он не может. */
  cabinetTripQuestion: (id: string, text: string) =>
    http<{ ok: true }>(`/cabinet/trips/${encodeURIComponent(id)}/question`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  /** Карточка договора с вложениями. 404 — договор не этой организации. */
  cabinetContract: (id: string) =>
    http<CabinetContractDetail>(`/cabinet/contracts/${encodeURIComponent(id)}`),
  /** Приложить файл к своему договору (multipart, поле "file"). Тип проверяется по содержимому. */
  cabinetAttach: (id: string, form: FormData) =>
    upload<{ uploadId: string; name: string }>(
      `/cabinet/contracts/${encodeURIComponent(id)}/attachments`,
      form,
    ),
  /**
   * Файл вложения своего договора. Blob, а не URL: ручка требует `Authorization`, поэтому
   * простым `<a href>` его не взять — вызывающий отдаёт blob браузеру (см. downloadWithAuth).
   */
  cabinetAttachment: async (uploadId: string): Promise<Blob> => {
    const res = await fetchTracked(`/cabinet/attachments/${encodeURIComponent(uploadId)}`, {
      headers: await authHeaders(),
    })
    if (!res.ok) {
      if (res.status === 401) notifyUnauthorized()
      throw new ApiError(errorMessage(res, await res.text().catch(() => '')), res.status)
    }
    return res.blob()
  },

  /**
   * Пригласить коллегу из своей организации. ВÖİN не передаётся: бэк берёт его из карточки
   * приглашающего, иначе ответственный одной организации приводил бы людей в чужую.
   * Право ответственного — специалисту ответят 422.
   */
  cabinetInvite: (input: {
    fullName: string
    email: string
    phone?: string
    role: 'SIGNER' | 'SPECIALIST'
  }) =>
    http<{ id: string; status: string }>('/cabinet/invites', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Анкета на доступ — ручка ПУБЛИЧНАЯ, токена нет. Ответ всегда одинаков: принято ли,
   * есть ли уже доступ и есть ли заявка — снаружи неразличимо (иначе перебор пар
   * «ВÖİN + почта» превращает анкету в справочник).
   */
  cabinetSignup: (input: {
    fullName: string
    email: string
    phone?: string
    voen: string
    role: 'SIGNER' | 'SPECIALIST'
  }) => publicHttp<{ accepted: true; message: string }>('/external-access/requests', input),

  // ── подписание через SIMA İmza ──
  /** Операции подписания: `assigned` — ждут моей подписи (по умолчанию), `initiated` — мои. */
  signingOperations: (scope?: 'assigned' | 'initiated') =>
    http<SigningOperation[]>(`/signing/operations${qs({ scope })}`),
  /** Подтверждение входа через SIMA: операция Auth на любой личный сертификат. */
  createSigningAuth: () =>
    http<SigningOperation>('/signing/operations', {
      method: 'POST',
      body: JSON.stringify({ type: 'Auth' }),
    }),
  /** Кого можно назвать подписантом: сотрудники с подтверждённым в SIMA сертификатом. */
  signingSigners: () => http<{ id: string; fullName: string }[]>('/signing/signers'),
  /**
   * Отправить вложение заявки на подпись (шаг «Подписанный экземпляр» договора). Ручка в ЭДО:
   * права считает он (участник заявки), заявку операции называет сервер — из самого вложения.
   * Подписанный файл НЕ прикладывается сам: его resultUploadId уходит в решение шага как скан.
   */
  signAttachment: (uploadId: string, signerUserId?: string) =>
    http<SigningOperation>(`/edo/attachments/${uploadId}/sign`, {
      method: 'POST',
      body: JSON.stringify({ signerUserId }),
    }),
  /**
   * Ссылка на публичную страницу подписания для КОНТРАГЕНТА: учётной записи у него нет, поэтому
   * подписанта сервер называет сам — фильтром `o_<VÖEN>` по ВÖİN из самой заявки. `signerFin` —
   * для случая, когда контрагент подписывает личным сертификатом.
   */
  signAttachmentLink: (uploadId: string, signerFin?: string) =>
    http<SigningOperation & { publicUrl: string; publicUrlExpiresAt: string }>(
      `/edo/attachments/${uploadId}/sign-link`,
      { method: 'POST', body: JSON.stringify({ signerFin }) },
    ),
  /** Карточка операции: статус, подписант, отчёт проверки подписей и ссылка талона для QR. */
  signingOperation: (id: string) => http<SigningOperation>(`/signing/operations/${id}`),
  /**
   * Подписанный документ операции. Приватный файл ЭДО: ручка требует Bearer и проверяет права
   * (инициатор, подписант, админ), поэтому публичный api.fileUrl() не подходит. Отдаём Blob —
   * вызывающий сам делает URL.createObjectURL и обязан revokeObjectURL после использования.
   */
  signingDocument: async (id: string): Promise<Blob> => {
    const res = await fetchTracked(`/signing/operations/${id}/document`, {
      headers: await authHeaders(),
    })
    if (!res.ok) {
      if (res.status === 401) notifyUnauthorized()
      throw new ApiError(errorMessage(res, await res.text().catch(() => '')), res.status)
    }
    return res.blob()
  },

  /**
   * Публичная страница подписания `/s/<токен>`: ни Bearer, ни учётной записи — пропуск в самом
   * токене. Негодный или истёкший токен сервер отдаёт как 404, страница так и говорит.
   */
  publicSigning: (token: string) =>
    http<PublicSigningCard>(`/signing/public/${encodeURIComponent(token)}`),
  /**
   * Адрес документа по той же ссылке — не Blob: его показывает <iframe> на вебе и открывает
   * системный просмотрщик на телефоне, а оба умеют только URL. Ручка публичная, заголовков
   * не нужно. До подписи это исходный документ, после — подписанный экземпляр.
   */
  publicSigningDocumentUrl: (token: string) =>
    `${BASE}/signing/public/${encodeURIComponent(token)}/document`,

  // ── preferences ──
  preferences: () => http<UserPreferences>('/users/me/preferences'),
  updatePreferences: (patch: UserPreferencesPatch) =>
    http<UserPreferences>('/users/me/preferences', { method: 'PATCH', body: JSON.stringify(patch) }),

  // ── feed ── (курсорная пагинация: { items, nextCursor })
  posts: (channel?: string, cursor?: string, limit?: number) =>
    http<PostPage>(`/feed/posts${qs({ channel, cursor, limit })}`),
  post: (id: string) => http<PostDetail>(`/feed/posts/${id}`),
  createPost: (data: PostInput) =>
    http<Post>('/feed/posts', { method: 'POST', body: JSON.stringify(data) }),
  /** author или admin. */
  updatePost: (id: string, data: Partial<PostInput>) =>
    http<Post>(`/feed/posts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deletePost: (id: string) => http<void>(`/feed/posts/${id}`, { method: 'DELETE' }),
  addComment: (id: string, body: string) =>
    http<Comment>(`/feed/posts/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  updateComment: (postId: string, commentId: string, body: string) =>
    http<Comment>(`/feed/posts/${postId}/comments/${commentId}`, {
      method: 'PATCH', body: JSON.stringify({ body }),
    }),
  deleteComment: (postId: string, commentId: string) =>
    http<void>(`/feed/posts/${postId}/comments/${commentId}`, { method: 'DELETE' }),
  react: (id: string, type: ReactionType) =>
    http<ReactionCounts>(`/feed/posts/${id}/react`, { method: 'POST', body: JSON.stringify({ type }) }),

  // ── uploads / files ──
  /** multipart → дескриптор файла; затем передать id в attachmentIds поста. */
  // Вложение поста: purpose=post — публичный файл для <img> в ленте. Без назначения API отвечает
  // 400 (раньше файл становился `generic`, а `generic` отдавался всем по ссылке).
  uploadFile: (form: FormData) => upload<Attachment>('/uploads?purpose=post', form),
  /** Публичная отдача файла — кладётся прямо в <img src> / <Image source>. */
  fileUrl: (id: string) => `${BASE}/files/${id}`,
  /** Абсолютизирует относительный URL из API (coverUrl / attachment.url / avatarUrl приходят как /api/files/:id). */
  assetUrl: (u?: string | null) =>
    !u ? undefined : /^https?:\/\//.test(u) ? u : `${ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`,

  // ── learning / LMS ──
  courses: (category?: string) => http<Course[]>(`/learning/courses${qs({ category })}`),
  course: (id: string) => http<CourseDetail>(`/learning/courses/${id}`),
  enrollCourse: (id: string) =>
    http<{ enrolled: true; progress: number }>(`/learning/courses/${id}/enroll`, { method: 'POST' }),
  setCourseProgress: (id: string, progress: number) =>
    http<{ progress: number; completedAt: string | null }>(`/learning/courses/${id}/progress`, {
      method: 'PATCH', body: JSON.stringify({ progress }),
    }),
  learningSummary: () => http<LearningSummary>('/learning/me/summary'),

  // ── calendar ──
  calendarEvents: (from: string, to: string) =>
    http<CalEvent[]>(`/calendar/events${qs({ from, to })}`),
  calendarUpcoming: (limit?: number) => http<CalEvent[]>(`/calendar/upcoming${qs({ limit })}`),
  /** Производственный календарь: тип каждого дня в [from, to] (праздник/выходной/сокращённый + переносы). */
  timeOffProductionDays: (from: string, to: string, employmentId?: string, onBehalfOfUserId?: string) =>
    http<ProductionDay[]>(`/edo/time-off/production-days${qs({ from, to, employmentId, onBehalfOfUserId })}`),
  /** Без `calendar` бэк берёт календарь текущего пользователя (у филиала Грузии — GE). */
  productionDays: (from: string, to: string, calendar?: string) =>
    http<ProductionDay[]>(`/calendar/production-days${qs({ from, to, calendar })}`),

  // ── calendar: mail-import (Microsoft 365 / Graph, 🔶) ──
  /** Статус подключения почтового календаря. */
  graphStatus: () => http<GraphStatus>('/calendar/graph/status'),
  /** URL согласия Entra — фронт открывает его (expo-web-browser openAuthSessionAsync). */
  graphConnect: () => http<GraphConnect>('/calendar/graph/connect'),
  /** Кнопка «Обновить» — инкрементальная синхронизация по deltaLink. */
  graphSync: () => http<GraphSyncResult>('/calendar/graph/sync', { method: 'POST' }),
  /** Отключить почтовый календарь (удаляет GRAPH-события). */
  graphDisconnect: () => http<void>('/calendar/graph/connection', { method: 'DELETE' }),

  // ── communities ──
  communities: () => http<Community[]>('/communities'),
  community: (id: string) => http<CommunityDetail>(`/communities/${id}`),
  joinCommunity: (id: string) =>
    http<{ joined: true; members: number }>(`/communities/${id}/join`, { method: 'POST' }),
  leaveCommunity: (id: string) =>
    http<{ joined: false; members: number }>(`/communities/${id}/join`, { method: 'DELETE' }),

  // ── help / faq ──
  faq: (category?: string) => http<Faq[]>(`/help/faq${qs({ category })}`),

  // ── kb (база знаний, PoC: headless-прокси к BookStack) ──
  kbConfig: () => http<KbConfig>('/kb/config'),
  kbBooks: () => http<KbBook[]>('/kb/books'),
  kbBook: (id: number) => http<KbBookDetail>(`/kb/books/${id}`),
  kbPage: (id: number) => http<KbPage>(`/kb/pages/${id}`),
  kbSearch: (q: string) => http<KbSearchHit[]>(`/kb/search${qs({ q })}`),

  // ── my tasks (личный todo, не ЭДО) ──
  tasks: () => http<MyTask[]>('/tasks'),
  createTask: (data: MyTaskInput) =>
    http<MyTask>('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (id: string, data: Partial<MyTaskInput>) =>
    http<MyTask>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTask: (id: string) => http<void>(`/tasks/${id}`, { method: 'DELETE' }),

  // ── global search (⌘K) ──
  search: (q: string, init?: { signal?: AbortSignal }) =>
    http<SearchResults>(`/search${qs({ q })}`, { signal: init?.signal }),

  // ── news (🔶) ── (курсорная пагинация: { items (без body), nextCursor })
  news: (cursor?: string, limit?: number, includeDrafts?: boolean) =>
    http<NewsPage>(`/news${qs({ cursor, limit, includeDrafts: includeDrafts ? 'true' : undefined })}`),
  /** Деталь — с полем body. Черновик → 404 для не-admin. */
  newsItem: (id: string) => http<NewsItem>(`/news/${id}`),
  /** admin: создать. coverId — из attachmentIds, иначе берётся первая картинка. */
  createNews: (data: NewsInput) =>
    http<NewsItem>('/news', { method: 'POST', body: JSON.stringify(data) }),
  /** admin: правка; attachmentIds заменяет весь набор. */
  updateNews: (id: string, data: Partial<NewsInput>) =>
    http<NewsItem>(`/news/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteNews: (id: string) => http<void>(`/news/${id}`, { method: 'DELETE' }),

  // ── edo ──
  createVacation: (data: VacationPayload) =>
    http<EdoRequest>('/edo/vacation', { method: 'POST', body: JSON.stringify(data) }),
  /** Остаток отпускных дней из 1С — «графа остатка» формы отпуска (ТЗ-2026-004 §3.2). asOf=YYYY-MM-DD. */
  vacationBalance: (asOf?: string, employmentId?: string, onBehalfOfUserId?: string) =>
    http<VacationBalance>(`/edo/vacation/balance${qs({ asOf, employmentId, onBehalfOfUserId })}`),
  /** Бухгалтер начисляет отпускные (ТЗ-2026-004 шаг 11) → payment_status Paid. vacationPay справочно. */
  accrueVacation: (id: string, vacationPay?: number) =>
    http<RequestCard>(`/edo/requests/${id}/accrue`, {
      method: 'POST',
      body: JSON.stringify(vacationPay != null ? { vacationPay } : {}),
    }),
  /** Остаток месячного лимита отгулов — дёрнуть перед показом формы (ТЗ §3.1). 400 без 1С-externalId.
   *  period=YYYY-MM — месяц выбранной даты: лимит помесячный, у следующего месяца он свой. */
  timeOffBalance: (period?: string, employmentId?: string, onBehalfOfUserId?: string) =>
    http<TimeOffBalance>(`/edo/time-off/balance${qs({ period, employmentId, onBehalfOfUserId })}`),
  /** Справочник причин отсутствия для dropdown (ТЗ §3.2) — массив строк; слать значение, не индекс. */
  timeOffReasons: () => http<string[]>('/edo/time-off/reasons'),
  /** Отгул: цепочка рук → (Рук.СП) → HR, без подписи GD. Возвращает карточку заявки. */
  createTimeOff: (data: TimeOffInput) =>
    http<RequestCard>('/edo/time-off', { method: 'POST', body: JSON.stringify(data) }),
  /** Больничный лист: рук → HR (приказ) → REGISTERED. overlaps — мягкое предупреждение, заявка создаётся всегда. */
  createSickLeave: (data: SickLeaveInput) =>
    http<RequestCard & { overlaps: AbsenceOverlap[] }>('/edo/sick-leave', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  /** Отцовский отпуск (ТЗ-2026-014): uploadId справки ОБЯЗАТЕЛЕН — грузить через uploadEdoDoc. */
  createPaternityLeave: (data: PaternityLeaveInput) =>
    http<RequestCard>('/edo/paternity-leave', { method: 'POST', body: JSON.stringify(data) }),
  /** Материнский отпуск (ТЗ-2026-012): мед. основание вносит HR вручную, портал его не шлёт. */
  createMaternityLeave: (data: MaternityLeaveInput) =>
    http<RequestCard>('/edo/maternity-leave', { method: 'POST', body: JSON.stringify(data) }),
  /** Уход за ребёнком (ТЗ-2026-013): данные ребёнка обязательны; dateTo пуст — бессрочный уход. */
  createChildCareLeave: (data: ChildCareLeaveInput) =>
    http<RequestCard>('/edo/child-care-leave', { method: 'POST', body: JSON.stringify(data) }),
  /** Документ произвольной формы: маршрут согласования (approvers[] по порядку) задаёт инициатор. */
  createFreeform: (data: FreeformInput) =>
    http<RequestCard>('/edo/freeform', { method: 'POST', body: JSON.stringify(data) }),
  /**
   * Согласование договора (ТЗ-2026-015): типизированная форма + маршрут от инициатора,
   * строго последовательно. Проект договора обязателен вложением (uploadIds ≥ 1).
   */
  createContract: (data: ContractInput) =>
    http<RequestCard>('/edo/contract', { method: 'POST', body: JSON.stringify(data) }),
  /**
   * Карточка контрагента по ВÖİN (ТЗ-2026-015 §7) — реквизиты и риск-статус из e-taxes.gov.az.
   * 404 — такого ВÖİN в реестре нет, 503 — госпортал недоступен либо проверка выключена:
   * оба случая означают «заполните форму вручную», подачу заявки они не блокируют.
   * `refresh` — принудительная пересверка (кнопка «проверить риск», §7.3).
   */
  lookupCounterparty: (voen: string, refresh?: boolean) =>
    http<CounterpartyCard>('/counterparties/lookup', {
      method: 'POST',
      body: JSON.stringify(refresh ? { voen, refresh } : { voen }),
    }),
  /** Командировка (ТЗ-2026-003): 8 шагов, employeeIds[] — групповая (пусто → сам инициатор). */
  createBusinessTrip: (data: BusinessTripInput) =>
    http<RequestCard>('/edo/business-trip', { method: 'POST', body: JSON.stringify(data) }),
  /** Направления командировок (страна/город + суточные из норм 1С) для пикера. */
  tripDestinations: () => http<TripDestination[]>('/edo/business-trip/destinations'),
  /**
   * Маршрут БУДУЩЕЙ заявки — «кто согласует» до отправки формы. Те же шаги, что `route[]`
   * карточки. `grade` — код грейда вакансии; `company` — подписант договора, определяющий
   * его обязательный маршрут.
   */
  routePreview: (
    type: EdoType,
    grade?: string,
    onBehalfOfUserId?: string,
    employmentId?: string,
    company?: ContractCompany,
  ) =>
    http<EdoRouteStep[]>(`/edo/route-preview${qs({ type, grade, onBehalfOfUserId, employmentId, company })}`),
  /**
   * Валюты, доступные договору выбранной компании (ТЗ-2026-015). Набор разный в каждой
   * бухгалтерской базе 1С, и валюта вне справочника базы роняет проводку уже ПОСЛЕ полного
   * круга согласования. Пустой список = «ограничивать нечем» (у компании нет базы либо зеркало
   * молчит) — форма показывает полный набор.
   */
  contractCurrencies: (company: string) =>
    http<{ currencies: string[] }>(`/edo/contract/currencies${qs({ company })}`),
  /**
   * Страны для зарубежного контрагента — справочник 1С базы компании. `required` — договор
   * компании проводится в 1С, и вид, страна и адрес зарубежного контрагента обязательны.
   */
  contractCountries: (company: string) =>
    http<{ required: boolean; countries: ContractCountry[] }>(`/edo/contract/countries${qs({ company })}`),
  /** ЭДО-роли меня (справочник + оверрайды) — гейт UI: пикер «Подать от имени» (роль proxy). */
  myEdoRoles: () => http<{ roles: string[] }>('/edo/my-roles'),
  myRequests: () => http<EdoRequest[]>('/edo/requests'),
  /** «С моим участием» — документы, где я инициатор/согласующий/подписант (в т.ч. уже обработанные). */
  myParticipating: () => http<ParticipatingRequest[]>('/edo/participating'),
  edoRequest: (id: string) => http<RequestCard>(`/edo/requests/${id}`),
  terminationReferences: (employmentId?: string) => http<TerminationReferences>(`/edo/termination/references${qs({ employmentId })}`),
  createTerminationDraft: (data: TerminationDraftInput) => http<EdoRequest>('/edo/termination/drafts', { method: 'POST', body: JSON.stringify(data) }),
  saveTermination: (id: string, data: TerminationInput) => http<EdoRequest>(`/edo/termination/${id}/save`, { method: 'POST', body: JSON.stringify(data) }),
  withdrawTermination: (id: string) => http<EdoRequest>(`/edo/termination/${id}/withdraw`, { method: 'POST' }),
  /** Подача черновика увольнения на согласование (ТЗ-2026-008): основание 69.1 и срок проверит сервер. */
  submitTermination: (id: string) => http<RequestCard>(`/edo/termination/${id}/submit`, { method: 'POST' }),
  // Увольнение по инициативе работодателя: черновик и форма руководителя; подача и отзыв — общие ручки выше.
  terminationEmployerReferences: (q: { employeeId: string; employmentId?: string; targetEmploymentId?: string }) =>
    http<EmployerTerminationReferences>(`/edo/termination/employer/references${qs(q)}`),
  createTerminationEmployerDraft: (data: EmployerTerminationDraftInput) =>
    http<EdoRequest>('/edo/termination/employer/drafts', { method: 'POST', body: JSON.stringify(data) }),
  saveTerminationEmployer: (id: string, data: EmployerTerminationInput) =>
    http<EdoRequest>(`/edo/termination/employer/${id}/save`, { method: 'POST', body: JSON.stringify(data) }),
  /** Статьи ТК АР для раздела HR: справочник базы увольняемого без оснований срочного договора. */
  terminationEmployerArticles: (id: string) => http<EmployerArticles>(`/edo/termination/employer/${id}/articles`),
  /** Результат Exit-интервью — решение approve шага EXIT_INTERVIEW. */
  exitInterview: (taskId: string, exitInterview: ExitInterviewInput) =>
    http<RequestCard>(`/edo/tasks/${taskId}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision: 'approve', exitInterview }),
    }),
  hireAccess: (id: string) => http<{ canPrepare: boolean; canEdit: boolean; canCancel: boolean; canRecoverApproval?: boolean; linkedHireId?: string | null }>(`/edo/hire/${id}/access`),
  recoverHireApproval: (id: string) => http<RequestCard>(`/edo/hire/${id}/recover-approval`, { method: 'POST' }),
  directHireAccess: (employmentId?: string) => http<{ canPrepare: boolean }>('/edo/hire/direct-access', { method: 'POST', body: JSON.stringify({ employmentId }) }),
  prepareDirectHire: (data: DirectHireInput) => http<RequestCard>('/edo/hire/direct-drafts', { method: 'POST', body: JSON.stringify(data) }),
  prepareHire: (recruitmentRequestId: string, employmentId?: string) => http<RequestCard>('/edo/hire/drafts', { method: 'POST', body: JSON.stringify({ recruitmentRequestId, employmentId }) }),
  hireReferences: (id: string) => http<HireReferences>(`/edo/hire/${id}/references`),
  // ФИН — в теле: строка запроса оседает в логах прокси и в мониторинге (reportApiCall шлёт путь).
  hireFin: (id: string, fin: string) => http<HireFinResult>(`/edo/hire/${id}/fin`, { method: 'POST', body: JSON.stringify({ fin }) }),
  hireMainEmployees: (id: string, fin: string) => http<{ id: string; name: string }[]>(`/edo/hire/${id}/main-employees`, { method: 'POST', body: JSON.stringify({ fin }) }),
  saveHire: (id: string, data: Partial<HireInput>) => http<RequestCard>(`/edo/hire/${id}/save`, { method: 'POST', body: JSON.stringify(data) }),
  submitHire: (draftRequestId: string, data: HireInput, employmentId?: string) => http<RequestCard>('/edo/hire', { method: 'POST', body: JSON.stringify({ ...data, draftRequestId, employmentId }) }),
  cancelHire: (id: string, comment: string) => http<RequestCard>(`/edo/hire/${id}/cancel`, { method: 'POST', body: JSON.stringify({ comment }) }),
  /** Пнуть текущего согласующего. */
  remindRequest: (id: string) => http<void>(`/edo/requests/${id}/remind`, { method: 'POST' }),
  myTasks: () => http<EdoTask[]>('/edo/tasks'),
  /**
   * Решение по задаче. `uploadId` обязателен на шаге приказа командировки (стадия ORDER):
   * скан подписанного приказа — без него бэк вернёт 400 (ТЗ-2026-003 v1.2 §2.1).
   */
  /**
   * Ответ куратора HR на уточнение агентства. Право и наличие открытого вопроса проверяет бэк;
   * на фронте панель показывается по флагу карточки `canAnswerAgency`.
   */
  edoAgencyAnswer: (id: string, text: string) =>
    http<RequestCard>(`/edo/requests/${encodeURIComponent(id)}/agency-answer`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  decideTask: (
    id: string,
    decision: EdoDecision,
    comment?: string,
    /**
     * Сканы шага: приказы на шаге ORDER (несколько, 28.09.2026) или подписанный экземпляр
     * договора на SIGNED_COPY (ровно один — 1С забирает один скан). На прочих шагах бэк их игнорирует.
     */
    scanUploadIds?: string[],
    /**
     * Правка документа при переотправке с доработки (К1): исправленные поля payload и новые
     * вложения. Принимаются ТОЛЬКО на шаге REWORK вместе с decision='resubmit' — на прочих
     * шагах бэк отвечает 400. Что именно можно править, решает реестр процессов на сервере;
     * маршрут согласования не правится никогда.
     */
    revise?: { payload?: Record<string, unknown>; uploadIds?: string[] },
    /**
     * Срок действия договора: собирается на шаге автора SIGNED_COPY вместе со сканом
     * подписанного экземпляра (решение бизнеса 27.08.2026, перенос на автора — 23.09.2026).
     * На прочих шагах бэк его игнорирует.
     */
    dates?: { dateFrom: string; dateTo: string },
    /**
     * Замещающий в заявлении на отпуск: меняет HR на своём шаге при `approve`. Строка — новый
     * замещающий, `null` — снять, `undefined` — не трогать (ключа в теле нет). На любом другом
     * шаге и решении бэк отвечает 400.
     */
    deputyId?: string | null,
    /**
     * Командировка, волна 1: варианты поездки на шаге TRIP_OPTIONS и номер выбранного на
     * TRIP_CHOICE. Одним объектом, чтобы не растить список позиционных аргументов.
     */
    trip?: { variants?: TripVariantInput[]; variantIndex?: number },
    /** Раздел HR увольнения по инициативе работодателя — на шаге HR вместе с `approve`. */
    terminationHr?: TerminationHrInput,
  ) =>
    http<RequestCard>(`/edo/tasks/${id}/decide`, {
      method: 'POST',
      body: JSON.stringify({
        decision,
        comment,
        scanUploadIds: scanUploadIds?.length ? scanUploadIds : undefined,
        payload: revise?.payload,
        uploadIds: revise?.uploadIds?.length ? revise.uploadIds : undefined,
        dateFrom: dates?.dateFrom,
        dateTo: dates?.dateTo,
        deputyId,
        variants: trip?.variants?.length ? trip.variants : undefined,
        variantIndex: trip?.variantIndex,
        terminationHr,
      }),
    }),
  /** Взять групповую задачу на себя (pull) — закрепить за собой. 409, если её уже взял другой. */
  claimTask: (id: string) =>
    http<RequestCard>(`/edo/tasks/${id}/claim`, { method: 'POST' }),
  /** Вернуть групповую задачу в группу (снять с себя). Только текущий исполнитель или admin. */
  unclaimTask: (id: string) =>
    http<RequestCard>(`/edo/tasks/${id}/unclaim`, { method: 'POST' }),
  /** Назначить/переназначить задачу конкретному исполнителю (push; лид группы или admin). */
  assignTask: (id: string, userId: string) =>
    http<RequestCard>(`/edo/tasks/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  /** Кандидаты для назначения (пикер). canAssign=false → не лид, список пуст. */
  taskCandidates: (id: string) =>
    http<TaskCandidates>(`/edo/tasks/${id}/candidates`),

  // ── edo: подбор персонала (ТЗ-2026-011) ──
  /** Справочник грейдов вакансии — радио-группа «Уровень должности»; triggersCeo → добавляется шаг ГД. */
  recruitmentGrades: () => http<RecruitmentGrade[]>('/edo/recruitment/grades'),
  /** Дашборд ОИ/HR (ФТ-13) — заявки с фильтрами + срок в статусе + статистика. Только HR/admin. */
  recruitmentDashboard: (f?: { department?: string; grade?: string; status?: string }) =>
    http<RecruitmentDashboard>(`/edo/recruitment/dashboard${qs({ ...f })}`),
  /** Заявка на подбор: рук → (ГД по грейду) → ОИ/HR → поиск → закрытие. Портальный, без 1С. */
  createRecruitment: (data: RecruitmentInput) =>
    http<RequestCard>('/edo/recruitment', { method: 'POST', body: JSON.stringify(data) }),
  /** Закрытие с данными кандидата. resumeUploadId ОБЯЗАТЕЛЕН (файл заранее загружен как edo_doc). */
  closeRecruitment: (id: string, data: CloseRecruitmentInput) =>
    http<RequestCard>(`/edo/recruitment/${id}/close`, { method: 'POST', body: JSON.stringify(data) }),
  /** Отзыв заявки (любой тип; пока не ушла в 1С). Подбор — до старта поиска / ОИ/HR. Причина обязательна. */
  cancelRequest: (id: string, comment: string) =>
    http<RequestCard>(`/edo/requests/${id}/cancel`, { method: 'POST', body: JSON.stringify({ comment }) }),
  /**
   * Приказы закрытого шага приказа: итоговый список uploadId (оставляемые + новые). Чего нет в
   * списке — бэк отвязывает от заявки. Только HR компании заявки / admin (флаг canEditOrderScans).
   */
  updateOrderScans: (id: string, uploadIds: string[]) =>
    http<RequestCard>(`/edo/requests/${id}/order-scans`, { method: 'PUT', body: JSON.stringify({ uploadIds }) }),
  /** Добавить согласующего в идущий документ со своим маршрутом (FREEFORM/CONTRACT). */
  addApprover: (id: string, userId: string, position: 'next' | 'end') =>
    http<RequestCard>(`/edo/requests/${id}/approvers`, { method: 'POST', body: JSON.stringify({ userId, position }) }),

  // ── edo: IT-работы (ТЗ-2026-АКТ001) ──
  /** Справочники формы по направлению: модули, подрядчики, авторы, утверждающие, получатели — только активные. */
  itWorksOptions: (direction: ItWorksDirection) =>
    http<ItWorksOptions>(`/edo/it-works/options${qs({ direction })}`),
  /** Новый черновик (DRAFT без номера). Форма может быть неполной — обязательно лишь направление. */
  createItWorksDraft: (data: ItWorksInput) =>
    http<RequestCard>('/edo/it-works/drafts', { method: 'POST', body: JSON.stringify(data) }),
  /** Сохранить черновик целиком. 409 «Черновик изменился» — это уже не редактируемый черновик. */
  saveItWorks: (id: string, data: ItWorksInput) =>
    http<RequestCard>(`/edo/it-works/${id}/save`, { method: 'POST', body: JSON.stringify(data) }),
  /**
   * Подать черновик в процесс. Ошибки правил формы — 400: `message` = первая ошибка, все по полям —
   * в `ApiError.errors` (`{ field: 'modules[1].approverId', message }`).
   */
  submitItWorks: (id: string) =>
    http<RequestCard>(`/edo/it-works/${id}/submit`, { method: 'POST' }),
  /** Удалить черновик (204). Поданную заявку не удаляют — её отзывают; черновик не отзывается вовсе. */
  deleteItWorksDraft: (id: string) => http<void>(`/edo/it-works/${id}`, { method: 'DELETE' }),
  /** Решения согласующего по своим строкам этапа модулей (1–50); `rework` требует комментарий. */
  decideItWorksLines: (id: string, decisions: ItWorksLineDecisionInput[]) =>
    http<RequestCard>(`/edo/it-works/${id}/lines`, { method: 'POST', body: JSON.stringify({ decisions }) }),
  /** Загрузка документа ЭДО (резюме кандидата и т.п.) → { id }. multipart, поле "file". */
  uploadEdoDoc: (form: FormData) => upload<Attachment>('/uploads?purpose=edo_doc', form),
  /**
   * Защищённое вложение ЭДО (резюме, скан приказа) — ручка требует Bearer и проверяет ACL
   * по участникам заявки, поэтому публичный api.fileUrl() не подходит. Отдаём Blob;
   * вызывающий сам делает URL.createObjectURL(...) и обязан revokeObjectURL после использования.
   */
  edoAttachment: async (uploadId: string): Promise<Blob> => {
    const res = await fetchTracked(`/edo/attachments/${uploadId}`, { headers: await authHeaders() })
    if (!res.ok) {
      if (res.status === 401) notifyUnauthorized()
      throw new ApiError(errorMessage(res, await res.text().catch(() => '')), res.status)
    }
    return res.blob()
  },

  // ── notifications ──
  notifications: (unread = false) =>
    http<Notification[]>(`/notifications${unread ? '?unread=true' : ''}`),
  unreadCount: () => http<{ count: number }>('/notifications/unread-count'),
  readNotification: (id: string) => http(`/notifications/${id}/read`, { method: 'POST' }),
  readAllNotifications: () => http('/notifications/read-all', { method: 'POST' }),
  /** Регистрировать Expo push-токены уже сейчас (отправка — фаза 2). */
  registerDevice: (data: DeviceRegistration) =>
    http<{ ok: true }>('/notifications/devices', { method: 'POST', body: JSON.stringify(data) }),
  unregisterDevice: (token: string) =>
    http<void>('/notifications/devices', { method: 'DELETE', body: JSON.stringify({ token }) }),

  // ── health ──
  health: () => http<{ status: string; db: string; camunda?: string }>('/health'),
}
